import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Grid from '@mui/material/Grid2';
import { Box, Stack, Skeleton, Typography, useTheme, useMediaQuery, Paper, Button } from '@mui/material';
import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import { formatDate } from '@/helpers/formatDate';
import useAlertStore from '@/stores/alertStore';
import { useAuthStore } from '@/stores/authStore';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import type { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import useFetchTranscript from '@/hooks/useFetchTranscript';
import { useSavedNotes } from '@/hooks/useSavedNotes';
import { useDesktopSharedNoteHandoff } from '@/hooks/useDesktopNoteHandoff';
import { truncateErrorMessage } from '@/helpers/errors';

import NoteHeader from '@/pages/Note/components/NoteHeader';
import DesktopHandoffInterstitial from '@/pages/Note/components/DesktopHandoffInterstitial';
import VideoPlayer from '@/pages/Note/components/VideoPlayer/VideoPlayer';
import AudioPlayer from '@/pages/Note/components/AudioPlayer/AudioPlayer';
import VideoChapters from '@/pages/Note/components/VideoPlayer/VideoChapters';
import AudioChapters from '@/pages/Note/components/AudioPlayer/AudioChapters';
import Summary from '@/pages/Note/components/Summary/Summary';
import RightTabs from '@/pages/Note/components/RightTabs';
import NoteContentTabs from '@/pages/Note/components/NoteContentTabs';
import { GridContainer } from '@/pages/Note/styles';
import { usePlayTracking } from '@/hooks/usePlayTracking';
import useFetchContentAnalysis from '@/hooks/useFetchContentAnalysis';
import ContentAnalysis from '@/pages/Note/components/DocumentChapters/ContentAnalysis';
import { useSharedNoteFormats } from '@/features/noteFormats';

const Shared = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 600px and below - true mobile
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { loading: authLoading, userLoggedIn, userData } = useAuthStore();
  const [noteData, setNoteData] = useState<NoteOut | null>(null);
  const [isLoadingNote, setIsLoadingNote] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState('');
  const [signedThumbnailUrl, setSignedThumbnailUrl] = useState<string | undefined>();
  const [sharedBy, setSharedBy] = useState<{ name: string; email?: string } | null>(null);
  const {
    saveNote,
    unsaveNote,
    isNoteSaved,
    isMutating: isSaveMutating,
    isLoading: isSavedLoading,
  } = useSavedNotes(userData?.id);
  const isSaved = token ? isNoteSaved(token) : false;
  const autoSaveAttemptedRef = useRef(false);

  // Auto-save the shared note to the user's watched list on page visit.
  // CRITICAL: wait for isSavedLoading to be false so the Firestore snapshot
  // has arrived — otherwise isSaved is unreliable and saveNote's ref check
  // sees an empty array, causing duplicates on every visit.
  const autoSaveNote = useCallback(async () => {
    if (!token || !userData?.id || !noteData || isSavedLoading || isSaved || autoSaveAttemptedRef.current) return;
    autoSaveAttemptedRef.current = true;
    try {
      await saveNote(token);
    } catch {
      // Silently fail — user can still manually save via bookmark icon
    }
  }, [token, userData?.id, noteData, isSavedLoading, isSaved, saveNote]);

  useEffect(() => {
    autoSaveNote();
  }, [autoSaveNote]);

  // Signed-out viewers get the public view of the same share — the mirror of
  // PublicNote forwarding signed-in viewers here. A /shared/ link is what the
  // address bar shows after a signed-in viewer opens a /public/ link, so it is
  // routinely forwarded to people with no NoteGo session.
  useEffect(() => {
    if (!authLoading && !userLoggedIn && token) {
      navigate(`/public/${token}`, { replace: true });
    }
  }, [authLoading, userLoggedIn, token, navigate]);

  useEffect(() => {
    // After a successful login and redirect, the user lands on this page.
    // We must clear the redirect flag from session storage to prevent
    // unwanted redirects on subsequent logins. This runs once when the
    // component mounts.
    if (sessionStorage.getItem('redirectAfterLogin')) {
      sessionStorage.removeItem('redirectAfterLogin');
    }
  }, []);

  useEffect(() => {
    // Ignore a response once the token has moved on: a slow fetch for A landing
    // after B's would put A's note — and A's creator, which the handoff hook
    // judges ownership by — under /shared/B.
    let cancelled = false;
    const fetchShared = async () => {
      setIsLoadingNote(true);
      setError(null);
      // Drop the previous note before fetching the next. This page stays
      // mounted across /shared/A -> /shared/B, and until B lands the handoff
      // hook would otherwise judge B's ownership by A's creator. Matches
      // useNotePageData on /note/:noteId.
      setNoteData(null);
      setSharedBy(null);
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/shared/${token}`);
        if (!res.ok) {
          const errorData = await res.json().catch(() => null);
          const detail = typeof errorData?.detail === 'string' ? errorData.detail : undefined;
          if (detail) {
            throw new Error(detail);
          } else if (res.status === 404) {
            throw new Error('This shared note no longer exists or the link is invalid.');
          } else if (res.status === 410) {
            throw new Error('This share link has expired. Ask the note owner to share it again.');
          } else {
            throw new Error('Failed to fetch shared note.');
          }
        }
        const data = await res.json();
        if (cancelled) return;

        // Set note data, whether it's the direct object or nested
        const noteData = data.note || data;
        setNoteData(noteData);

        // Extract shared_by information from the note
        if (noteData.shared_by && noteData.shared_by.first_name) {
          setSharedBy({
            name: `${noteData.shared_by.first_name || ''} ${noteData.shared_by.last_name || ''}`.trim(),
            email: noteData.shared_by.email,
          });
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred.';
        setError(errorMessage);
      } finally {
        if (!cancelled) setIsLoadingNote(false);
      }
    };
    if (token) fetchShared();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const { isLoading: isLoadingTranscript, error: transcriptError } = useFetchTranscript(
    noteData?.transcript_url || null,
  );
  const {
    transcriptList: fullTranscriptList,
    isLoading: isLoadingFullTranscript,
    error: fullTranscriptError,
  } = useFetchTranscript(noteData?.full_transcript_url || null);

  // Call usePlayTracking hook at the top level (before any returns)
  const { trackPlay } = usePlayTracking({
    note: noteData,
    isShared: true,
    shareToken: token,
  });

  const { initialAssessment } = useFetchContentAnalysis(
    noteData?.content_analysis,
    noteData?.full_transcript_url || noteData?.transcript_url,
  );

  // Fetch alternate formats for the shared note (read-only, public endpoint)
  useSharedNoteFormats(token ?? '');

  // Offer the note to the desktop app when this computer has one that can take
  // a share link, and only for notes someone else shared — never your own.
  // This is where a signed-in viewer lands from /public/:token, so it is the
  // share-link counterpart of the offer on /note/:noteId. Purely additive: the
  // page below renders and stays usable whatever the handoff does.
  const handoff = useDesktopSharedNoteHandoff(token, noteData?.creator_user_id);

  useEffect(() => {
    if (transcriptError) showAlert('Error loading key points.', 'error');
    if (fullTranscriptError) showAlert('Error loading key points.', 'error');
  }, [transcriptError, fullTranscriptError, showAlert]);

  useEffect(() => {
    // Reset the resolved URLs on every note change, and ignore in-flight
    // resolutions once the note has moved on. Without this, SPA navigation from
    // /shared/A to /shared/B kept note A's video playing under note B (and a
    // slow resolve for A could land after B's and overwrite it).
    // Matches PublicNote.tsx and useNotePageData.ts.
    setSignedUrl('');
    setSignedThumbnailUrl(undefined);

    if (!noteData) return;

    let cancelled = false;

    const filePath = noteData.video_url || noteData.audio_url || '';
    if (filePath) {
      resolveMediaUrl(filePath)
        .then((url) => {
          if (!cancelled && url) setSignedUrl(url);
        })
        .catch(() => {
          if (!cancelled) showAlert('Error loading media.', 'error');
        });
    }

    // Fetch thumbnail URL independently - silent failure (progressive enhancement)
    if (noteData.thumbnail_url) {
      resolveMediaUrl(noteData.thumbnail_url)
        .then((url) => {
          if (!cancelled && url) setSignedThumbnailUrl(url);
        })
        .catch(() => {
          // Silently ignore - older notes may not have thumbnails
        });
    }

    return () => {
      cancelled = true;
    };
  }, [noteData, showAlert]);

  if (isLoadingNote || authLoading) {
    return (
      <Stack alignItems={isMobile ? 'flex-start' : 'center'} margin={isMobile ? '8px' : '32px'}>
        <Box sx={{ width: '100%', maxWidth: '1200px', mb: 1 }}>
          <Skeleton variant="text" width="60%" height={36} />
          <Skeleton variant="text" width="25%" height={20} sx={{ mt: 0.5 }} />
        </Box>
        <GridContainer container spacing={{ xs: 0, md: 3 }}>
          <Grid
            size={{ xs: 12, sm: 12, md: 8.4 }}
            sx={{
              '@media (max-width: 600px)': {
                paddingRight: '0 !important',
                paddingLeft: '0 !important',
                width: '100%',
                '& > *': { maxWidth: '100%' },
              },
            }}
          >
            <Box
              sx={{
                maxWidth: isMobile ? '100%' : '800px',
                borderRadius: isMobile ? '12px' : '22px',
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="rectangular"
                width="100%"
                sx={{
                  aspectRatio: '16 / 9',
                  height: 'auto',
                  maxHeight: isMobile ? '60vh' : '70vh',
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 3.6 }} sx={{ mt: { xs: 3, sm: 3, md: 0 } }}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
          </Grid>
        </GridContainer>
      </Stack>
    );
  }
  if (error) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh', px: { xs: 2, sm: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
            p: { xs: 3, sm: 4 },
            borderRadius: '23px',
            border: '1px solid',
            borderColor: 'neutral.otherBorder',
            boxShadow: '0px 4px 20px 0px rgba(129, 129, 129, 0.12)',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'neutral.lightBackground',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
            }}
          >
            <LinkOffRounded sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'grey.900' }}>
            Hmm, we can't find this note
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.600', mb: 2 }}>
            The link may have expired or the note is no longer shared. Ask the person who shared it to send a fresh
            link.
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 3 }}>
            {truncateErrorMessage(error)}
          </Typography>
          <Button
            variant="contained"
            href="/my-notes"
            sx={{
              px: 3,
              py: 1,
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Go to My Notes
          </Button>
        </Paper>
      </Stack>
    );
  }
  if (!noteData) return null;

  const isAudioOnly = Boolean(noteData.audio_url) && !noteData.video_url;
  const isProcessing = false;

  const handleToggleSave = async () => {
    if (!token || isSaveMutating) return;
    try {
      if (isSaved) {
        await unsaveNote(token);
        showAlert('Note removed from saved list.', 'success');
      } else {
        await saveNote(token);
        showAlert('Note saved for later.', 'success');
      }
    } catch {
      showAlert('Something went wrong. Try again later.', 'error');
    }
  };

  return (
    <Stack alignItems={isMobile ? 'flex-start' : 'center'} margin={isMobile ? '8px' : '32px'}>
      {/* This route renders NoteHeader without NoteLayout, so it owns its own
          meta tags — NoteLayout is what supplies them on /note/:id. */}
      <Helmet>
        <title>{noteData.title || 'Shared note'}</title>
        <meta name="description" content={noteData.summary || ''} />
        <meta property="og:title" content={noteData.title || 'Shared note'} />
        <meta property="og:description" content={noteData.summary || ''} />
        <meta name="twitter:title" content={noteData.title || 'Shared note'} />
        <meta name="twitter:description" content={noteData.summary || ''} />
      </Helmet>
      {/* Second gate beside the hook's own: this route renders signed-out too. */}
      {userData && <DesktopHandoffInterstitial {...handoff} />}
      <NoteHeader
        title={noteData.title || ''}
        date={formatDate(noteData.created_at || '')}
        isOwner={false}
        isProcessing={isProcessing}
        isLoadingNote={isLoadingNote}
        sharedBy={sharedBy}
        onToggleSave={userData ? handleToggleSave : undefined}
        isSaved={isSaved}
        isSaveMutating={isSaveMutating}
      />
      <GridContainer container spacing={{ xs: 0, md: 3 }}>
        <Grid
          size={{ xs: 12, sm: 12, md: 8.4 }}
          sx={{
            '@media (max-width: 600px)': {
              paddingRight: '0 !important',
              paddingLeft: '0 !important',
              width: '100%',
              '& > *': {
                maxWidth: '100%',
              },
            },
          }}
        >
          {signedUrl ? (
            isAudioOnly ? (
              <AudioPlayer src={signedUrl} chapters={noteData.document_chapter} />
            ) : (
              <VideoPlayer
                src={signedUrl}
                chapters={noteData.document_chapter}
                isMobile={isMobile}
                onPlay={trackPlay}
                posterUrl={signedThumbnailUrl}
                noteId={noteData.id}
                shareToken={token}
              />
            )
          ) : (
            <Box
              sx={{
                maxWidth: isMobile ? '100%' : '800px',
                borderRadius: isMobile ? '12px' : '22px',
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="rectangular"
                width="100%"
                sx={{
                  aspectRatio: '16 / 9',
                  height: 'auto',
                  maxHeight: isMobile ? '60vh' : '70vh',
                }}
              />
            </Box>
          )}
          <NoteContentTabs readOnly>
            <Summary
              content={noteData.summary || ''}
              isOwner={false}
              isProcessing={isProcessing}
              isLoadingNote={isLoadingNote}
            />
            {noteData.document_chapter &&
              (isAudioOnly ? (
                <AudioChapters chapters={noteData.document_chapter} isProcessing={false} />
              ) : (
                <VideoChapters chapters={noteData.document_chapter} isProcessing={false} />
              ))}
            {initialAssessment && <ContentAnalysis initialAssessment={initialAssessment} />}
          </NoteContentTabs>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 12, md: 3.6 }}
          sx={{
            // add small top margin when stacked on mobile
            mt: { xs: 3, sm: 3, md: 0 },
            '@media (max-width: 600px)': {
              paddingLeft: '0 !important',
            },
          }}
        >
          <RightTabs
            tags={noteData.tags || []}
            isAudioOnly={isAudioOnly}
            isOwner={false}
            isProcessing={isProcessing}
            isLoading={isLoadingNote || isLoadingTranscript || isLoadingFullTranscript}
            fullTranscriptList={fullTranscriptList}
            keyMoments={noteData.key_moments || null}
            isPublicView
            noteId={noteData.id}
            shareToken={token}
          />
        </Grid>
      </GridContainer>
    </Stack>
  );
};

export default Shared;
